import React, { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { useHotkeys } from 'react-hotkeys-hook';
import { Tooltip } from '@mui/material';

import { getAllRolesThunk, saveUser } from '../../../store/user/userSlice';
import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { patchOrganization } from '../../../api/organizationsApi';
import { NOTIFICATION } from '../../../enums/ENUMS';
import UserOrgInputField from './UserOrgInputField';
import styles from './CreateUserModal.module.scss';
import { ORGANIZATION } from '../../../enums';
import { getMyUserInfo } from '../../../api';
import Modal from '../../modules/Modal';

type TCreateUserModal = {
  open: boolean;
  switchModal: () => void;
  fetchData: () => void;
  myOrganization: ORGANIZATION;
  customOrgId?: string;
  type: 'parent' | 'child';
};

type TNewUser = {
  username: string;
  email: string;
  role_id: string;
  allow_download_own_composers?: boolean;
};

const CreateOrgUserModal: React.FC<TCreateUserModal> = ({ open, switchModal, fetchData, myOrganization, customOrgId, type }) => {
  const allRoles = useAppSelector((state) => state.user.allRoles);
  const notification = useSnackbar();
  const submitButtonRef = useRef<HTMLDivElement>(null);
  const okButtonRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);

  const [newUsers, setNewUsers] = useState<TNewUser[]>([{ username: '', email: '', role_id: '', allow_download_own_composers: true }]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const addUser = () => {
    setNewUsers((prevData) => [...prevData, { username: '', email: '', role_id: '', allow_download_own_composers: true }]);
  };

  const removeUser = (i: number) => {
    setNewUsers((prevData) => {
      const newUsers = [...prevData];
      newUsers.splice(i, 1);
      return newUsers;
    });
  };

  const [showLimitModal, setShowLimitModal] = useState(false);

  const dispatch = useAppDispatch();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);
    try {
      const dataToSend = newUsers.map((obj: TNewUser, i: number) => ({
        username: obj.username.trim(),
        email: obj.email.toLocaleLowerCase().replace(/\s/g, ''),
        role_id: obj.role_id,
        allow_download_own_composers: obj.allow_download_own_composers,
      }));
      let modifiedUsers: any = [];
      if (myOrganization.users) {
        myOrganization.users.forEach((u) => {
          if (u) {
            modifiedUsers.push(u);
          }
        });
      }
      await patchOrganization(myOrganization.id, { newUsers: dataToSend, users: modifiedUsers });
      notification.showMessage(NOTIFICATION.UPDATED);
      setTimeout(async () => {
        await fetchData();
        const userJson = await getMyUserInfo();
        dispatch(saveUser(userJson.data));
      }, 1700);
      onClose();
    } catch (err) {
      console.log(err);

      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        if (err?.response?.data?.message.includes('Users limit')) {
          setShowLimitModal(true);
        } else {
          // @ts-ignore
          notification.showMessage(err?.response?.data?.message);
        }
      } else {
        notification.showMessage(NOTIFICATION.NOT_UPDATED);
      }
    } finally {
      setLoading(false);
    }
  };

  const onClose = () => {
    switchModal();
    setNewUsers([{ username: '', email: '', role_id: '', allow_download_own_composers: true }]);
    setErrors({});
  };

  const onLimitPopupDismiss = () => {
    setShowLimitModal(false);
    onClose();
  };

  const [showWarningText, setShowWarningText] = useState(false);
  useEffect(() => {
    if (myOrganization) {
      if (allRoles?.length === 0) {
        dispatch(getAllRolesThunk());
      }
      const emptyUserSlots = myOrganization.users_limit - myOrganization.total_users;
      if (emptyUserSlots === 0) {
        setShowLimitModal(true);
      }
      if (newUsers.length > emptyUserSlots) {
        setShowWarningText(true);
      } else {
        setShowWarningText(false);
      }
    }
  }, [myOrganization, newUsers]);

  const onUserSet = (value: { username: string; email: string; role_id: string; allow_download_own_composers?: boolean }, index: number) => {
    const newUsersCopy = [...newUsers];
    newUsersCopy[index] = value;
    setNewUsers(newUsersCopy);
  };

  useHotkeys('Enter', () => {
    if (open && !loading)
      if (!showLimitModal) {
        submitButtonRef?.current?.click();
      } else {
        okButtonRef?.current?.click();
      }
  });

  return (
    <Modal open={open} onClose={onClose} loading={loading}>
      {!showLimitModal ? (
        <div className={styles.modalBody}>
          <div className={styles.modalHeader}>Add new user to {myOrganization.name}</div>
          <form className={styles.form} onSubmit={onSubmit}>
            {newUsers.map((user, index) => (
              <UserOrgInputField
                key={index}
                onUserSet={onUserSet}
                allRoles={allRoles}
                removeUser={removeUser}
                index={index}
                errors={errors}
                customOrgId={customOrgId}
                ignoreRole
                orgType={type}
              />
            ))}

            {showWarningText && (
              <div className={styles.warningText}>
                Users limit reached ({myOrganization.total_users + newUsers.length} / {myOrganization.users_limit})! Need more Users?
                Contact Fulcrum to adjust your subscription!
              </div>
            )}

            <div className={styles.customPadding}>
              <Tooltip title={`${myOrganization.total_users + newUsers.length}  / ${myOrganization.users_limit} limit`}>
                <div className={styles.addMore} onClick={addUser}>
                  + Add one more?
                </div>
              </Tooltip>

              <div className={styles.actionBtns}>
                <ContainedButton disabled={!newUsers.length || loading} type="submit">
                  <div ref={submitButtonRef}>Create</div>
                </ContainedButton>
                <OutlinedButton onClick={onClose}>Cancel</OutlinedButton>
              </div>
            </div>
          </form>
        </div>
      ) : (
        <div className={styles.modalBody}>
          <div className={styles.modalHeader}>
            <div style={{ fontWeight: 700, marginBottom: '16px' }}>Users limit reached!</div>
            <div>Need more Users? Contact Fulcrum to adjust your subscription!</div>
          </div>
          <div className={styles.customPadding}>
            <div className={styles.actionBtns}>
              <ContainedButton onClick={onLimitPopupDismiss}>
                <div ref={okButtonRef}>OK</div>
              </ContainedButton>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default CreateOrgUserModal;
